import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, Navigate } from 'react-router-dom';
import DocumentScanner from '../components/DocumentScanner';
import SwipeableEvent from '../components/SwipeableEvent';
import IncomingCards from '../components/IncomingCards';
import ManualEventForm from '../components/ManualEventForm';
import EditEventModal from '../components/EditEventModal';
import GoogleCalendarButton from '../components/GoogleCalendarButton';
import EmailButton from '../components/EmailButton';
import { useFamilyRole } from '../hooks/useFamilyRole';


function Upload({
  inputText,
  setInputText,
  isLoading,
  error,
  extractDates,
  pendingEvents,
  acceptEvent,
  ignoreEvent,
  getCategoryStyles,
  formatDateTime,
  API_KEY,
  isMobile,
  eventToEdit,
  setEventToEdit,
  updateAcceptedEvent
}) {
  // All hooks must be called before any conditional returns
  const { canCreateEvents, loading } = useFamilyRole();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('manual');
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const tabsRef = useRef(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Set initial tab based on URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');

    if (tabParam && ['manual', 'paste', 'upload', 'email', 'gcal'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location]);

  // Effect to handle tab transitions
  useEffect(() => {
    // Map tab names to indices
    const tabIndices = {
      'manual': 0,
      'paste': 1,
      'upload': 2
    };

    setActiveTabIndex(tabIndices[activeTab] || 0);
  }, [activeTab]);

  // Extract URL parameters for pre-filling form
  const params = new URLSearchParams(location.search);
  const initialDate = params.get('date');
  const initialHour = params.get('hour');
  
  // If user is a child (can't create events), redirect to home
  if (!loading && !canCreateEvents) {
    return <Navigate to="/" replace />;
  }
  
  
  // We no longer set the manual tab active when editing an event
  // The edit functionality is now handled by EditEventModal only
  
  // Style definitions
  const sectionHeadingStyle = {
    fontSize: '1.1rem',
    fontWeight: '600',
    marginBottom: '0.8rem',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '0.5rem',
  };

  // Style for the buttons container
  const buttonsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? 'repeat(5, 1fr)' : 'repeat(5, minmax(140px, 180px))',
    gap: isMobile ? '8px' : '12px',
    marginBottom: '1.5rem',
    width: isMobile ? '95%' : 'auto',
    justifyContent: 'center',
    margin: '0 auto',
  };
  
  // Style for individual buttons
  const buttonStyle = (isActive) => ({
    padding: isMobile ? '0.4rem 0.15rem' : '0.8rem 1.2rem',
    cursor: 'pointer',
    backgroundColor: isActive ? 'var(--primary-color)' : 'var(--card-bg)',
    color: isActive ? 'white' : 'var(--text-color)',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    borderRadius: '8px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: isMobile ? '0.15rem' : '0.6rem',
    fontSize: isMobile ? '0.65rem' : '0.95rem',
    border: isActive ? 'none' : '1px solid var(--border-color)',
    boxShadow: isActive ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
    height: isMobile ? 'auto' : '48px',
  });
  
  // Style for the bulk action buttons
  const bulkActionButtonStyle = {
    backgroundColor: 'var(--card-bg)',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 0.75rem',
    color: 'var(--text-color)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: isMobile ? '0.8rem' : '0.85rem',
    transition: 'all 0.2s ease',
  };
  
  const acceptAllButtonStyle = {
    ...bulkActionButtonStyle,
    borderColor: 'rgba(46, 204, 113, 0.5)',
    color: '#2ecc71',
  };
  
  const ignoreAllButtonStyle = {
    ...bulkActionButtonStyle,
    borderColor: 'rgba(231, 76, 60, 0.5)',
    color: '#e74c3c',
  };
  
  // Tab content container style
  const tabContentStyle = {
    backgroundColor: 'var(--card-bg)',
    borderRadius: '8px',
    padding: isMobile ? '0.8rem' : '1.2rem',
    width: isMobile ? '92%' : '100%', // Full width on desktop now that it's in a column
    boxSizing: 'border-box',
    marginBottom: '1rem', // Reduced bottom margin
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    border: '1px solid var(--border-color)',
    margin: isMobile ? '0 auto' : '0', // Center only on mobile
  };
  
  // Removed animation for tab content transitions

  // Handle adding a manually created event (no longer handles editing)
  const handleAddManualEvent = (eventData) => {
    console.log('🔶 handleAddManualEvent called with:', eventData);
    console.log('➕ Adding new event:', eventData.eventName);

    // We're adding a new event
    // Pass the entire event object to be added directly to accepted events
    acceptEvent(eventData);

    // We no longer need to navigate from here as App.js handles it
    /* Navigation handled by App.js */
  };
  
  // Handle file upload - automatically process after upload
  const handleTextExtracted = (extractedText) => {
    // Check for source metadata
    let sourceType = 'document';
    let cleanText = extractedText;
    
    // Check if source metadata is included
    if (extractedText.includes('__SOURCE:')) {
      // Extract the source type
      const sourceMatch = extractedText.match(/__SOURCE:([A-Z]+)__/);
      if (sourceMatch && sourceMatch[1]) {
        sourceType = sourceMatch[1].toLowerCase();
      }
      
      // Remove the metadata tag
      cleanText = extractedText.replace(/__SOURCE:[A-Z]+__/, '').trim();
    }
    
    // Set the input text without metadata
    setInputText(cleanText);
    
    // Extract dates with source information
    extractDates(cleanText, sourceType);
  };

  return (
    <>
      {/* Add CSS animations for the "all caught up" card */}
      <style>
        {`
          @keyframes ripple {
            0% {
              transform: scale(0.8);
              opacity: 1;
            }
            100% {
              transform: scale(2.4);
              opacity: 0;
            }
          }
          
          @keyframes pulseScale {
            0% {
              transform: scale(0);
              opacity: 0;
            }
            50% {
              transform: scale(1.1);
              opacity: 1;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
          
          @keyframes checkPop {
            0% {
              stroke-dashoffset: 50;
            }
            100% {
              stroke-dashoffset: 0;
            }
          }
        `}
      </style>
      
      {/* Main page container with extra bottom padding for scrolling */}
      <div className="page-container upload-page-container" style={{ 
        paddingBottom: '150px', // Extra padding to prevent content from being hidden behind bottom nav
      display: 'flex',
      flexDirection: 'column',
      width: '100%'
    }}>
      {/* Top spacer - adds space before the tab buttons */}
      <div style={{ paddingTop: '20px' }}></div>
      
      {/* Tab navigation buttons - switches between Manual, Paste, and Upload modes */}
      <div className="upload-buttons-container" style={{
        ...buttonsContainerStyle,
        padding: isMobile ? '0 15px' : '0'
      }}>
        {/* Manual tab button - shows manual event form */}
        <button 
          style={buttonStyle(activeTab === 'manual')}
          onClick={() => setActiveTab('manual')}
        >
          {/* List icon */}
          <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "18" : "16"} height={isMobile ? "18" : "16"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="8" y1="6" x2="21" y2="6"></line>
            <line x1="8" y1="12" x2="21" y2="12"></line>
            <line x1="8" y1="18" x2="21" y2="18"></line>
            <line x1="3" y1="6" x2="3.01" y2="6"></line>
            <line x1="3" y1="12" x2="3.01" y2="12"></line>
            <line x1="3" y1="18" x2="3.01" y2="18"></line>
          </svg>
          <span>Manual</span>
        </button>
        
        {/* Paste tab button - shows text area for pasting content */}
        <button 
          style={buttonStyle(activeTab === 'paste')}
          onClick={() => setActiveTab('paste')}
        >
          {/* Clipboard icon */}
          <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "18" : "16"} height={isMobile ? "18" : "16"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
          </svg>
          <span>Paste</span>
        </button>
        
        {/* Upload tab button - shows file upload interface */}
        <button 
          style={buttonStyle(activeTab === 'upload')}
          onClick={() => setActiveTab('upload')}
        >
          {/* Upload icon */}
          <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "18" : "16"} height={isMobile ? "18" : "16"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <span>Upload</span>
        </button>
        
        {/* Calendar tab button - shows Google Calendar sync */}
        <button 
          style={buttonStyle(activeTab === 'gcal')}
          onClick={() => setActiveTab('gcal')}
        >
          {/* Calendar icon */}
          <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "18" : "16"} height={isMobile ? "18" : "16"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <span>Calendar</span>
        </button>
        
        {/* Email tab button - shows Gmail integration */}
        <button 
          style={buttonStyle(activeTab === 'email')}
          onClick={() => setActiveTab('email')}
        >
          {/* Email icon */}
          <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "18" : "16"} height={isMobile ? "18" : "16"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
            <polyline points="22,6 12,13 2,6"></polyline>
          </svg>
          <span>Email</span>
        </button>
      </div>
      
      {/* Main content area with two columns on desktop */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '0' : '20px',
        width: '100%',
        maxWidth: '1400px',
        margin: '0 auto',
        padding: isMobile ? '0' : '0 20px'
      }}>
        {/* Left column - Tab content (forms/controls) */}
        <div className="tab-content-container" style={{ 
          width: isMobile ? '100%' : '50%', 
          display: 'flex', 
          justifyContent: isMobile ? 'center' : 'flex-start',
          flexDirection: 'column'
        }}>
          {/* Desktop section header - shows current tab name */}
          {!isMobile && (
            <div style={{
              marginTop: '20px', // Container spacing to align with right column
              marginBottom: '0'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '-20px',
                marginBottom: '16px',
                backgroundColor: 'transparent',
                borderBottom: 'none',
                padding: '20px 16px 12px 16px',
                position: 'relative',
                zIndex: 15,
              }}>
                <h3 style={{
                  padding: '0',
                  margin: '0',
                  color: '#000000',
                  textShadow: '0 0 4px #ffffff, 0 0 2px #ffffff',
                  fontSize: '1.3rem',
                  fontWeight: '400'
                }}>{(() => {
                  switch(activeTab) {
                    case 'manual':
                      return 'Manual Entry';
                    case 'paste':
                      return 'Paste Text';
                    case 'upload':
                      return 'Upload Document';
                    case 'gcal':
                      return 'Google Calendar Sync';
                    case 'email':
                      return 'Gmail Integration';
                    default:
                      return 'Upload Form';
                  }
                })()}</h3>
                <p style={{
                  padding: '0',
                  margin: isMobile ? '-10px 0 0 0' : '0',
                  color: '#666666',
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  fontWeight: '400',
                  textAlign: 'center'
                }}>{(() => {
                  switch(activeTab) {
                    case 'manual':
                      return 'Fill out the form to create a new event';
                    case 'paste':
                      return 'Paste your text here and AI will extract an event';
                    case 'upload':
                      return 'Upload a document and AI will scan for events';
                    case 'gcal':
                      return 'Sync your Google Calendar to import events';
                    case 'email':
                      return 'Connect Gmail and AI will find school events';
                    default:
                      return 'Choose a method to add events';
                  }
                })()}</p>
              </div>
            </div>
          )}
          {/* Manual Event Form Tab - For creating new events only */}
          {activeTab === 'manual' && (
          <div 
            className="manual-tab-container"
            style={{ 
              marginBottom: isMobile ? '-20px' : '0', // No negative margin on desktop
              display: 'flex',
              justifyContent: 'center',
              width: '100%'
            }}
          > 
            <div className="manual-tab-inner-container" style={{ 
              width: isMobile ? '92%' : '100%',
              marginTop: isMobile ? '0' : '0',
              paddingTop: isMobile ? '0' : '0'
            }}>
              <div style={{ marginBottom: 0, marginTop: isMobile ? '0' : '0' }}>
              <div style={{ marginTop: isMobile ? '0' : '-20px' }}>
                  <ManualEventForm
                    onAddEvent={handleAddManualEvent}
                    API_KEY={API_KEY}
                    eventToEdit={null} // Always passing null - this form is for creating new events only
                    initialDate={initialDate}
                    initialHour={initialHour ? parseInt(initialHour) : null}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Paste Text Tab - Using same styling as Manual Form tab */}
        {activeTab === 'paste' && (
          <div className="paste-tab-container" style={{
            ...tabContentStyle,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(8px)',
            border: '2px solid #2A9D8F', // Green border using life type color
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            paddingBottom: '10px', // Further reduced padding
            marginBottom: isMobile ? '10px' : '-20px', // Reduced margin to move Incoming section higher on desktop
            marginTop: isMobile ? '20px' : '0' // No top margin on desktop
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#2A9D8F', // Green color text
              letterSpacing: '-0.01em',
              borderBottom: 'none', // Removed the border
              paddingBottom: '0.3rem',
              marginBottom: '0.5rem', // Reduced margin from 1rem to 0.5rem
              fontSize: '1.1rem',
              fontWeight: '600'
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
              </svg>
              Paste your text here:
            </div>
            <textarea
              id="textInput"
              className="manual-input"
              style={{ 
                minHeight: isMobile ? '150px' : '180px', // Reduced height
                width: '100%', 
                marginBottom: '0', // Reduced margin from 1rem to 0.7rem
                padding: '0.8rem',
                borderRadius: '8px',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.9rem',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)'
              }}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Paste any text containing dates and events..."
            />
            
            <button
              className="extract-button"
              onClick={() => extractDates()}
              disabled={isLoading}
              style={{
                padding: '0.6rem 1.2rem',
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                opacity: isLoading ? 0.7 : 1,
                boxShadow: '0 2px 5px rgba(42, 157, 143, 0.3)',
                width: '100%',
                marginTop: '10px', // Added space between textarea and button
                marginBottom: '10px' // Further reduced bottom margin
              }}
            >
              {isLoading ? (
                <>
                  <span className="button-spinner"></span>
                  <span style={{marginLeft: '8px'}}>Extracting...</span>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px'}}>
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="16"></line>
                    <line x1="8" y1="12" x2="16" y2="12"></line>
                  </svg>
                  Extract Dates
                </>
              )}
            </button>
          </div>
        )}
        
        {/* Upload tab content - document scanner with automatic date extraction */}
        {activeTab === 'upload' && (
          <div style={{
            ...tabContentStyle,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(8px)',
            border: '2px solid #2A9D8F', // Green border for "life" category color
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            paddingBottom: '5px', // Minimal bottom padding
            marginBottom: isMobile ? '5px' : '-20px', // Negative margin on desktop to bring content closer
            marginTop: isMobile ? '20px' : '0' // No top margin on desktop
          }}>
            {/* Section header with upload icon */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#2A9D8F', // Green text to match border
              letterSpacing: '-0.01em',
              borderBottom: 'none',
              paddingBottom: '0.2rem',
              marginBottom: '0.4rem',
              fontSize: '1.1rem',
              fontWeight: '600'
            }}>
              {/* Upload icon */}
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              Upload Document
            </div>
            {/* Document scanner component - handles file upload and OCR */}
            <DocumentScanner 
              onTextExtracted={handleTextExtracted}
              API_KEY={API_KEY}
              autoProcess={true}
            />
          </div>
        )}
        
        {/* Google Calendar tab content - sync with Google Calendar */}
        {activeTab === 'gcal' && (
          <div style={{
            ...tabContentStyle,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(8px)',
            border: '2px solid #1a73e8', // Google blue border
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            paddingBottom: '15px',
            marginBottom: isMobile ? '5px' : '-20px',
            marginTop: isMobile ? '20px' : '0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            {/* Section header with calendar icon */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#1a73e8', // Google blue text
              letterSpacing: '-0.01em',
              borderBottom: 'none',
              paddingBottom: '0.2rem',
              marginBottom: '0.4rem',
              fontSize: '1.1rem',
              fontWeight: '600',
              width: '100%'
            }}>
              {/* Calendar icon */}
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              Google Calendar Integration
            </div>
            {/* Description text */}
            <p style={{
              fontSize: '0.9rem',
              lineHeight: '1.5',
              color: '#666',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              Connect to your Google Calendar to import your events directly into this app.
            </p>
            {/* Google Calendar sync button component */}
            <GoogleCalendarButton
              onEventsImported={(googleEvents) => {
                if (googleEvents && googleEvents.length > 0) {
                  // Handle each imported event
                  googleEvents.forEach(event => {
                    acceptEvent(event);
                  });
                }
              }}
            />
          </div>
        )}
        
        {/* Email tab content - Gmail integration */}
        {activeTab === 'email' && (
          <div style={{
            ...tabContentStyle,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(8px)',
            border: '2px solid #DB4437', // Gmail red border
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            paddingBottom: '15px',
            marginBottom: isMobile ? '5px' : '-20px',
            marginTop: isMobile ? '20px' : '0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            {/* Section header with email icon */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#DB4437', // Gmail red color
              letterSpacing: '-0.01em',
              borderBottom: 'none',
              paddingBottom: '0.2rem',
              marginBottom: '0.4rem',
              fontSize: '1.1rem',
              fontWeight: '600',
              width: '100%'
            }}>
              {/* Email icon */}
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
              Gmail Integration
            </div>
            {/* Description text */}
            <p style={{
              fontSize: '0.9rem',
              lineHeight: '1.5',
              color: '#666',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              Connect your GMail account and AI will extract events from Seesaw or official school emails.
            </p>
            {/* Gmail sync button component */}
            <EmailButton
              onEventsExtracted={(emailEvents) => {
                if (emailEvents && emailEvents.length > 0) {
                  // Process extracted email events through extractDates
                  // which filters duplicates and adds to pending events
                  extractDates(null, null, emailEvents);
                }
              }}
            />
          </div>
        )}
        </div>
        
        {/* Right column - Incoming events section */}
        {(
          <div className="pending-events-container result-container" style={{
            width: isMobile ? '100%' : '50%',
            margin: '0 auto',
            marginTop: isMobile ? '25px' : '20px', // Aligns with left column on desktop
            marginBottom: isMobile ? '0' : '15px',
            position: 'relative',
            backgroundColor: '#f5f5f5',
            paddingTop: '0'
          }}>
          {/* Section header with "Incoming" title */}
          <div className="result-header" style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: isMobile ? 'flex-start' : 'center',
            marginTop: isMobile ? '-30px' : '-20px',
            marginBottom: isMobile ? '4px' : '16px',
            backgroundColor: 'transparent',
            borderBottom: 'none',
            padding: isMobile ? '4px 0' : '20px 16px 12px 16px',
            position: 'relative',
            zIndex: 15,
          }}>
            {/* "Incoming" heading */}
            <h3 style={{
              padding: '0',
              paddingLeft: isMobile ? '24px' : '0',
              margin: '0',
              color: '#000000',
              textShadow: '0 0 4px #ffffff, 0 0 2px #ffffff',
              fontSize: isMobile ? '1.2rem' : '1.3rem',
              fontWeight: isMobile ? '600' : '400'
            }}>Incoming</h3>
            <p style={{
              padding: '0',
              paddingLeft: isMobile ? '24px' : '0',
              margin: isMobile ? '-10px 0 0 0' : '0',
              color: '#666666',
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              fontWeight: '400'
            }}>New events will appear here for your approval.</p>
          </div>
          {/* List container for pending event cards */}
          <div className="events-list" style={{
            maxHeight: 'none',
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobile ? 'flex-start' : 'center',
            paddingLeft: isMobile ? '16px' : '0',
            paddingRight: isMobile ? '16px' : '0',
            paddingBottom: isMobile ? '30px' : '0' // Extra padding on mobile for bottom nav
          }}>
            {/* Map through pending events and render IncomingCards */}
            {pendingEvents.length > 0 ? (
              pendingEvents.map((event) => (
                <IncomingCards
                  key={event.id}
                  event={event}
                  onAccept={acceptEvent}
                  onIgnore={ignoreEvent}
                  onEdit={() => {
                    console.log("Setting event to edit:", event);
                    // Set the event to edit and open the modal
                    setEventToEdit(event);
                    setIsEditModalOpen(true);
                  }}
                  getCategoryStyles={getCategoryStyles}
                  formatDateTime={formatDateTime}
                />
              ))
            ) : (
              /* Empty state - You are all caught up */
              <div style={{
                background: '#238b45', // Vibrant royal green
                color: '#ffffff',
                borderRadius: '12px',
                width: isMobile ? 'calc(100% - 48px)' : '100%',
                boxSizing: 'border-box',
                padding: isMobile ? '32px 16px' : '48px 24px',
                boxShadow: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: isMobile ? '120px' : '160px',
                marginBottom: '16px',
                margin: isMobile ? '0 24px 16px 24px' : '0 0 16px 0',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Animated checkmark */}
                <div style={{
                  marginBottom: '16px',
                  position: 'relative',
                  width: isMobile ? '48px' : '56px',
                  height: isMobile ? '48px' : '56px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {/* Ripple effect */}
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.5)',
                    animation: 'ripple 1.5s ease-out 0.5s',
                    pointerEvents: 'none'
                  }} />
                  
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={isMobile ? '48' : '56'}
                    height={isMobile ? '48' : '56'}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.2))',
                      animation: 'pulseScale 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                      position: 'absolute',
                      zIndex: 2
                    }}
                  >
                    <circle 
                      cx="12" 
                      cy="12" 
                      r="10" 
                      strokeWidth="2"
                      fill="rgba(255, 255, 255, 0.1)"
                    />
                    <polyline 
                      points="8 12 11 15 16 10" 
                      strokeWidth="3"
                      fill="none"
                      style={{
                        strokeDasharray: '50',
                        strokeDashoffset: '50',
                        transformOrigin: 'center',
                        animation: 'checkPop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.4s forwards'
                      }}
                    />
                  </svg>
                </div>
                <h3 style={{
                  fontSize: isMobile ? '1.2rem' : '1.4rem',
                  fontWeight: '600',
                  margin: '0 0 8px 0',
                  textAlign: 'center',
                  color: '#ffffff',
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}>
                  You're All Caught Up!
                </h3>
                <p style={{
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  margin: '0',
                  textAlign: 'center',
                  color: '#ffffff'
                }}>
                  No new events to review
                </p>
              </div>
            )}
            <div style={{
              textAlign: 'center',
              fontSize: '0.8rem',
              color: '#888',
              fontStyle: 'italic',
              marginTop: '15px',
              width: '100%',
              paddingBottom: '10px'
            }}>
              Powered by OpenAI's GPT-4
            </div>
          </div>
        </div>
      )}
      </div>
      
      {error && <div className="error">{error}</div>}
      
      {/* Edit Event Modal - rendered in a portal for viewport positioning */}
      {isEditModalOpen && document.body && 
        createPortal(
          <EditEventModal
            event={eventToEdit}
            onSave={(updatedEvent) => {
              updateAcceptedEvent(updatedEvent);
              setIsEditModalOpen(false);
            }}
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
          />,
          document.body
        )
      }
    </div>
    </>
  );
}

export default Upload;